<template>
  <div id="app" :class="{ 'theme-sunny': isSunnyMode, 'theme-rainy': !isSunnyMode }">
    <NameReveal v-if="showNameReveal" @reveal-complete="onRevealComplete" />
    <template v-else>
      <Header />
      <main>
        <section id="home" class="section">
          <HomeView />
        </section>
        
        <section id="about" class="section">
          <AboutView />
        </section>
        
        <section id="projects" class="section">
          <ProjectsView />
        </section>
        
        <section id="contact" class="section">
          <ContactView />
        </section>
        
        <RouterView name="project-details" />
        
        <!-- Scroll to top button -->
        <div class="scroll-to-top" :class="{ 'visible': showScrollToTop }" @click="scrollToTop">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </div>
      </main>
      <Footer />
      <BackgroundEffect />
    </template>
  </div>
</template>

<script>
import { RouterView } from 'vue-router'
import { mapState } from 'pinia'
import { useThemeStore } from './stores/theme'
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import NameReveal from './components/NameReveal.vue'
import HomeView from './views/HomeView.vue'
import AboutView from './views/AboutView.vue'
import ProjectsView from './views/ProjectsView.vue'
import ContactView from './views/ContactView.vue'
import BackgroundEffect from './components/BackgroundEffect.vue'

export default {
  name: 'App',
  components: {
    Header,
    Footer,
    RouterView,
    NameReveal,
    HomeView,
    AboutView,
    ProjectsView,
    ContactView,
    BackgroundEffect
  },
  data() {
    return {
      showNameReveal: true,
      showScrollToTop: false
    }
  },
  computed: {
    ...mapState(useThemeStore, ['isSunnyMode'])
  },
  methods: {
    onRevealComplete() {
      this.showNameReveal = false
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    handleScroll() {
      if (window.scrollY > 500) {
        this.showScrollToTop = true;
      } else {
        this.showScrollToTop = false;
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="scss">
@use './assets/main.scss';

#app {
  min-height: 100vh;
  transition: background-color 0.5s ease, color 0.5s ease;
  position: relative;
  overflow-x: hidden;
}

.theme-sunny {
  --color-background: #fbfbf9;
  --color-background-rgb: 251, 251, 249;
  --color-text: #222222;
  --color-primary: #f8c630;
  --color-primary-rgb: 248, 198, 48;
  --color-secondary: #FF8B5E;
  --color-secondary-rgb: 255, 139, 94;
  --color-accent: #42b883;
  --color-border: #e2e2e0;
  --color-card-bg: rgba(251, 251, 249, 0.85);
  --color-shadow: rgba(0, 0, 0, 0.1);
  
  background-color: var(--color-background);
  color: var(--color-text);
}

.theme-rainy {
  --color-background: #2c3e50;
  --color-background-rgb: 44, 62, 80; /* RGB version for transparency/blur effects */
  --color-text: #f0f0f0;
  --color-primary: #3498db;
  --color-secondary: #9b59b6;
  --color-accent: #1abc9c;
  --color-border: #4a5568;
  --color-card-bg: rgba(52, 73, 94, 0.85);
  --color-shadow: rgba(0, 0, 0, 0.3);
  
  background-color: var(--color-background);
  color: var(--color-text);
}

main {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.section {
  padding: 4rem 0;
  scroll-margin-top: 80px; /* To account for the fixed header */
}

/* Container blur effect */
.container {
  position: relative;
  z-index: 1;
  padding: 20px;
  border-radius: 16px;
}

/* Scroll to top button */
.scroll-to-top {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background-color: var(--color-primary);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 10px var(--color-shadow);
  z-index: 99;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.scroll-to-top.visible {
  opacity: 1;
  transform: translateY(0);
}

.scroll-to-top svg {
  width: 24px;
  height: 24px;
  color: white;
}

/* Container and element styles */
.project-grid,
.skills-grid {
  position: relative;
  z-index: 1;
}
</style>